/**
 * Which straight-line events a manual grade downgrade should remove.
 *
 * Period, phase and bin-10 events are built from data graded above 1 (phases) or above 2
 * (periods and bin 10). Setting the brushed samples to 0 or 1 makes those events invalid when
 * they sit inside the brush. An event that only overlaps the edge, or that contains the brush,
 * is left in place — "within" means the event's own interval is inside the selection.
 *
 * A brush set to 0 also removes maneuver events whose climax falls inside it. The climax is
 * the mark, which sits inside the analysis window, so the window itself may stick out of the brush.
 */

export const ASSIGNABLE_GRADES = [0, 1, 2, 3] as const;

export type AssignableGrade = (typeof ASSIGNABLE_GRADES)[number];

/** Event types removed when a brush is graded 0 or 1. */
export const DOWNGRADE_EVENT_TYPES = ["PERIOD", "PHASE", "BIN 10"] as const;

/**
 * Maneuver dataset events removed when a brush is graded 0.
 * The climax sits `sec_before` after start_time. Those offsets are the analysis windows in
 * docs/analysis/maneuver-definitions.md, which is what the event's start_time is measured from.
 */
export const GRADE_ZERO_MANEUVER_EVENT_TYPES = ["TACK", "GYBE", "ROUNDUP", "BEARAWAY", "TAKEOFF"] as const;

const MANEUVER_CLIMAX_AFTER_START_SEC: Record<string, number> = {
  TACK: 15,
  GYBE: 15,
  ROUNDUP: 15,
  BEARAWAY: 15,
  TAKEOFF: 30,
};

export function isAssignableGrade(value: number): value is AssignableGrade {
  return Number.isInteger(value) && value >= 0 && value <= 3;
}

/** Grades 0 and 1 are the downgrades that invalidate straight-line events. */
export function isDowngradeGrade(grade: number): boolean {
  return grade === 0 || grade === 1;
}

export interface TimedEvent {
  event_id?: unknown;
  start_time?: unknown;
  end_time?: unknown;
  event_type?: unknown;
}

/**
 * True when the event interval is inside [startIso, endIso], inclusive.
 * Unparseable times are not a match.
 */
export function eventLiesWithinInterval(
  event: TimedEvent,
  startIso: string,
  endIso: string
): boolean {
  const eventStart = new Date(String(event.start_time ?? "")).getTime();
  const eventEnd = new Date(String(event.end_time ?? "")).getTime();
  const rangeStart = new Date(startIso).getTime();
  const rangeEnd = new Date(endIso).getTime();
  if (![eventStart, eventEnd, rangeStart, rangeEnd].every(Number.isFinite)) {
    return false;
  }
  if (eventEnd < eventStart || rangeEnd < rangeStart) {
    return false;
  }
  return eventStart >= rangeStart && eventEnd <= rangeEnd;
}

/**
 * Epoch ms of a maneuver's climax, `sec_before` after its start_time, or null when the type
 * or the start time cannot be read.
 */
export function maneuverClimaxMs(event: TimedEvent): number | null {
  const eventType = String(event.event_type ?? "").trim().toUpperCase();
  const offsetSec = MANEUVER_CLIMAX_AFTER_START_SEC[eventType];
  if (offsetSec === undefined) {
    return null;
  }
  const start = new Date(String(event.start_time ?? "")).getTime();
  if (!Number.isFinite(start)) {
    return null;
  }
  return start + offsetSec * 1000;
}

/**
 * Maneuver event ids whose climax falls inside a grade-0 brush, inclusive.
 * The analysis window is wider than the turn, so containment of start/end would keep a
 * maneuver whose mark sits in the section the user just zeroed.
 */
export function gradeZeroManeuverEventIds(
  events: ReadonlyArray<TimedEvent>,
  startIso: string,
  endIso: string
): number[] {
  const rangeStart = new Date(startIso).getTime();
  const rangeEnd = new Date(endIso).getTime();
  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd) || rangeEnd < rangeStart) {
    return [];
  }
  const wanted = new Set<string>(GRADE_ZERO_MANEUVER_EVENT_TYPES);
  const ids: number[] = [];
  for (const event of events) {
    const eventType = String(event.event_type ?? "").trim().toUpperCase();
    if (!wanted.has(eventType)) {
      continue;
    }
    const climax = maneuverClimaxMs(event);
    if (climax === null || climax < rangeStart || climax > rangeEnd) {
      continue;
    }
    const id = Number(event.event_id);
    if (Number.isInteger(id) && id > 0) {
      ids.push(id);
    }
  }
  return ids;
}

/** Integer event ids of period / phase / bin 10 rows that lie inside the downgraded brush. */
export function downgradeEventIds(
  events: ReadonlyArray<TimedEvent>,
  startIso: string,
  endIso: string
): number[] {
  const wanted = new Set<string>(DOWNGRADE_EVENT_TYPES);
  const ids: number[] = [];
  for (const event of events) {
    const eventType = String(event.event_type ?? "").trim().toUpperCase().replace(/\s+/g, " ");
    if (!wanted.has(eventType)) {
      continue;
    }
    if (!eventLiesWithinInterval(event, startIso, endIso)) {
      continue;
    }
    const id = Number(event.event_id);
    if (Number.isInteger(id) && id > 0) {
      ids.push(id);
    }
  }
  return ids;
}
